import css from '@emotion/css';
import styled from '@emotion/styled';
import { layers } from 'app/spacing';
import { labelCss, fontWeights } from 'app/typography';
import DateRangePicker from 'components/Kizen/DateRangePicker/DateRangePicker';
import EditableText from 'components/Kizen/EditableText';
import Icon from 'components/Kizen/Icon';
import Loader from 'components/Kizen/Loader';

export const HandleIcon = styled(Icon)`
  cursor: grab;
  position: absolute;
  left: 5px;
  top: 10px;
  height: 15px;
  z-index: 9;

  svg {
    color: var(--color-background-dark);
    transition: color 0.1s;
    height: 15px;
  }

  &:hover svg {
    color: var(--color-background-darker);
  }
  &:active svg {
    cursor: grabbing;
  }
`;

export const DashletContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

export const DashletHead = styled.div`
  display: flex;
  column-gap: 15px;
  z-index: ${layers.content(1)};
  align-items: start;
  padding-top: 10px;
  padding-right: 15px;
  padding-left: 15px;
  pointer-events: none;

  ${({ customStyles }) => css`
    background-color: ${customStyles?.backgroundColor ?? 'transparent'};
    color: ${customStyles?.textColor ?? 'var(--color-font-primary)'};
    border-radius: ${customStyles?.borderRadius ?? 0};
    padding-bottom: ${customStyles?.backgroundColor ? '11px' : '5px'};
    padding-top: calc((${customStyles.lineHeight} - 1em) / 2);
    padding-bottom: calc((${customStyles.lineHeight} - 1em) / 2);
  `};

  & * {
    pointer-events: all;
  }

  .IconButtonMenu__menu {
    z-index: 11; //ensure menu appears over gradients
  }

  .title-container.static-content {
    pointer-events: none;
  }
`;

export const DashletBody = styled.div`
  flex: 1;
  ${({ bottom }) =>
    typeof bottom === 'number' ? `padding-bottom: ${bottom}px;` : ''};
  left: 0;
  right: 0;
  overflow: hidden;
  ${({ isStaticBlock }) => (isStaticBlock ? '' : `padding: 0 15px;`)};
`;

export const DashletHeaderSection = styled.div`
  position: relative;
  display: flex;
  flex-grow: 1;
  ${({ mobile }) => (mobile ? 'flex-direction: column;' : '')};
  overflow: hidden;

  & > div {
    text-overflow: unset;
  }
`;

export const TitleContainer = styled.div`
  display: flex;
  flex-grow: 1;
  overflow: hidden;

  & > div {
    text-overflow: unset;
  }

  & > div > input {
    text-overflow: ${({ showEllipsis }) =>
      showEllipsis ? 'ellipsis' : 'unset'};
    padding-bottom: 1px;
    font-size: 12px;
  }
`;

export const DashletHeaderWrapper = styled.div`
  display: flex;
  text-align: center;
  height: 100%;
  flex-grow: 1;
  align-items: start;
  transition: opacity 0.2s;
  ${({ mobile }) => (mobile ? 'display: none' : '')};
  ${({ titleFocused }) => (titleFocused ? 'opacity: 0;' : 'opacity: 1;')};
`;

export const EditableTextEnd = styled.div`
  display: flex;
  width: 10px;
  position: relative;
`;

export const DashletControls = styled.div`
  position: absolute;
  right: ${({ rightOffset = 0 }) => `calc(100% + ${rightOffset}px)`};
  ${({ padding }) => (padding ? `padding-right: ${padding}px;` : '')}
  top: 0;
  display: flex;
  gap: 15px;
  align-items: flex-start;
  justify-content: flex-start;
  height: ${({ height }) => (height ? `${height}px` : 'auto')};

  &::before {
    content: '';
    position: absolute;
    left: -30px;
    top: 0;
    width: 30px;
    height: ${({ height }) => (height ? `${height}px` : 'auto')};
    background: ${({ customStyles }) =>
      `linear-gradient(to right, transparent, ${customStyles.backgroundColor ? `${customStyles.backgroundColor}, ${customStyles.backgroundColor}` : 'var(--color-background-white), var(--color-background-white)'})`};
    opacity: 0;
  }
`;

export const DashletInlineHeader = styled.div`
  position: relative;
  display: flex;
  z-index: ${layers.content(1)};
  align-items: center;
  column-gap: 15px;
  min-height: 20px;
`;

export const DashletCenterInfo = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: -5px;
  display: flex;
  pointer-events: none;
  height: ${({ height }) => (height ? `${height}px` : 'auto')};
`;

export const DashletFloatingCenter = styled.div`
  margin: 0 auto;
  margin-top: -5px;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
  background-color: ${({ customStyles }) => customStyles.backgroundColor};
  position: relative;
  color: ${({ customStyles }) =>
    customStyles.textColor
      ? customStyles.textColor
      : 'var(--color-font-primary)'};
  &::before {
    content: '';
    position: absolute;
    left: -60px;
    top: 0;
    width: 60px;
    height: 100%;
    background: ${({ customStyles }) =>
      `linear-gradient(to right, transparent, ${customStyles.backgroundColor ? `${customStyles.backgroundColor}, ${customStyles.backgroundColor}` : 'white, white'})`};
    opacity: 1;
  }

  &::after {
    content: '';
    position: absolute;
    left: 100%;
    top: 0;
    width: 60px;
    height: 100%;
    background: ${({ customStyles }) =>
      `linear-gradient(to left, transparent, ${customStyles.backgroundColor ? `${customStyles.backgroundColor}, ${customStyles.backgroundColor}` : 'white, white'})`};
    opacity: 1;
  }
`;

export const EditableTitle = styled(EditableText)`
  margin-top: 3px;
  margin-left: 4px;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  color: ${({ customStyles }) =>
    customStyles ? customStyles.textColor : 'var(--color-font-primary)'};
  input {
    background-image: none;
    ${labelCss('small')}
    ${({ customStyles }) => css`
      font-size: ${customStyles?.fontSize || labelCss('small')} !important;
      color: ${customStyles?.textColor || 'var(--color-font-primary)'};
      line-height: ${customStyles?.lineHeight || 'normal'};
      ${customStyles.titleAllCaps === false && 'text-transform: none;'}
      ${customStyles.fontFamily && `font-family: ${customStyles.fontFamily};`}
      ${customStyles.italicTitle && 'font-style: italic;'}
            font-weight: ${customStyles.boldTitle
        ? `${fontWeights.bold}`
        : `${fontWeights.light}`};
    `}
    padding-left: 0;
  }
  input:disabled {
    background-color: transparent;
  }
`;

export const NoDashlet = styled.div({
  flex: 1,
  cursor: 'pointer',
  span: {
    textAlign: 'left',
    lineHeight: 1.25,
  },
  '.padded-plus': {
    position: 'absolute',
    left: '50%',
    marginLeft: '-1rem',
    top: '50%',
    marginTop: '-1rem',
    color: 'var(--color-button-primary-default)',
  },
  '.padded-plus:hover': {
    color: 'var(--color-button-primary-hover)',
  },
  '.padded-plus:active': {},
});

export const StyledLoader = styled(Loader)`
  position: absolute;
  background-color: var(--color-background-white);
  top: 13%; // best value for masking loader of chart
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${layers.content(1)};
`;

export const DashletFilterShortcutButton = styled.div`
  position: relative;

  // FontAwesome v7 requires fixed width for fa-layers since SVGs are now position: absolute
  --fa-width: 1.25em;

  ${({ hasFilters }) =>
    hasFilters
      ? css`
          margin-right: 10.08px;

          && {
            opacity: 1;
          }
        `
      : css`
          margin-right: -2px;
        `}
  ${({ canEdit, hasFilters }) =>
    !canEdit
      ? css`
          pointer-events: none;
          ${!hasFilters
            ? css`
                &&& {
                  opacity: 0;
                }
              `
            : ''}
        `
      : ''}

  ${({ onClick }) =>
    onClick
      ? css`
          cursor: pointer;
          &:hover svg {
            color: var(--color-background-darker);
          }
        `
      : css`
          &:hover svg {
            color: var(--color-background-dark);
          }
        `}
`;

export const DashletFilterCount = styled.div`
  position: absolute;
  right: -10px;
  top: -4px;
  width: 13px;
  height: 13px;
  background-color: var(--color-background-darker);
  color: var(--color-background-white);
  border-radius: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  ${({ scale }) =>
    scale
      ? css`
          scale: ${scale};
          right: calc(-16px * ${scale});
        `
      : ''}
  ${({ offset }) =>
    offset
      ? css`
          top: calc(-4px + ${offset}px);
        `
      : ''}
`;

export const PillDateRangePicker = styled(DateRangePicker)`
  ${({ paddingLeft }) =>
    paddingLeft ? `padding-left: ${paddingLeft}px;` : ''};

  & > button > i {
    display: none;
  }

  & > button > div {
    transform: none;
    background-color: var(--color-background-dark);
  }

  ${({ dashletUnits }) =>
    dashletUnits <= 2
      ? css`
          & > button > div {
            max-width: 50px;
          }

          & > button > div > p {
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
          }
        `
      : ''}

  & > button:hover > div {
    background-color: var(--color-background-darker);
  }

  ${({ canEdit }) =>
    !canEdit &&
    css`
      pointer-events: none;
      & > button > div {
        background-color: var(--color-background-darker);
      }
    `}

  & > button > div {
    position: relative;
    left: unset;
    top: unset;
  }
`;

export const StyledPillContainer = styled.div`
  padding-left: 15px;
  padding-bottom: 5px;
  .kds-button.kds-button-text {
    color: var(--color-font-placeholder);
  }

  .kds-button.kds-button-text:hover {
    color: var(--color-font-primary);
  }

  ${({ isAbsolute }) =>
    isAbsolute
      ? css`
          position: absolute;
          bottom: 0;
          left: 0;
          min-height: 40px;
        `
      : ''}
`;
